/**
 * Shared contract for moderator control of participant mic/camera.
 *
 * Two scopes of state, deliberately kept in different places:
 *
 *   Room metadata      → `RoomMediaPolicy`. Room-wide "mute everyone" policy.
 *                        Durable and delivered by LiveKit in the JoinResponse,
 *                        so a participant who joins an hour later still sees it
 *                        with no moderator online and no extra round trip.
 *
 *   Participant metadata → `ParticipantMediaOverride`. Per-person override.
 *                        SESSION-SCOPED: LiveKit rebuilds participant metadata
 *                        from the token's metadata claim on a fresh join, so an
 *                        override written mid-call is lost if that person fully
 *                        rejoins (a signal reconnect keeps it). An exemption
 *                        that must outlive a rejoin has to be stored on room
 *                        metadata instead, keyed by participant identity.
 *                        TRI-STATE — `false` locked, `true` exempt, absent
 *                        follows the room policy. The absent case is why this
 *                        can't be a boolean: under a room-wide mute, "nobody
 *                        has said anything about this person" and "a moderator
 *                        explicitly allowed this person" must resolve
 *                        differently.
 *
 * NOTE: this file is duplicated verbatim in the Beam Live mobile app at
 * `mobile/src/helpers/mediaPolicy.ts` (separate repo, no shared package).
 * Any change here must be mirrored there or the two clients will disagree
 * about who is muted.
 */

/** Room-wide policy, stored on LiveKit room metadata. */
export interface RoomMediaPolicy {
  muteAll?: boolean;
  cameraOffAll?: boolean;
}

/** Per-participant override, stored on LiveKit participant metadata. */
export interface ParticipantMediaOverride {
  /** false = locked, true = exempt from room policy, absent = follow room. */
  micEnabled?: boolean;
  /** false = locked, true = exempt from room policy, absent = follow room. */
  cameraEnabled?: boolean;
  /**
   * Screen share. Only two of the three states are meaningful here: there is no
   * room-wide screen-share policy, so `absent` just means "not granted", and for
   * a restricted participant that reads the same as `false`.
   */
  screenShareEnabled?: boolean;
}

/**
 * Why a local control is locked.
 *   'disabled'     → a moderator turned this person's device off
 *   'server_muted' → a moderator server-muted this person's published track
 *   'muted_all'    → a room-wide mute is in effect; not personal
 *   'restricted'   → read-only page permission, or joined anonymously by link:
 *                    locked from the moment they arrive until a moderator
 *                    grants this specific device
 */
export type MediaLockReason =
  | ''
  | 'server_muted'
  | 'disabled'
  | 'muted_all'
  | 'restricted';

export interface MediaLock {
  locked: boolean;
  reason: MediaLockReason;
}

export const UNLOCKED: MediaLock = { locked: false, reason: '' };

/** Participant metadata also carries the Beam role; 'edit' means moderator. */
export interface ParticipantRoleMetadata {
  role?: string;
}

export const MODERATOR_ROLE = 'edit';

/** Beam page permission that can watch and listen but not contribute. */
export const READ_ONLY_ROLE = 'read';

/**
 * Someone who was let in off a shared link and has no Beam page permission at
 * all. Written into participant metadata when a waiting-room ticket is redeemed,
 * so the rest of the room can tell them apart from a member.
 */
export const GUEST_ROLE = 'guest';

export const isModeratorRole = (role?: string): boolean => role === MODERATOR_ROLE;

/**
 * Starts with everything off and needs a moderator to hand back each device
 * individually.
 *
 * Being admitted through the waiting room only buys a seat in the room — it is
 * deliberately not the same decision as being allowed to speak, so an admitted
 * guest lands here too.
 */
export const isRestrictedRole = (role?: string): boolean =>
  role === READ_ONLY_ROLE || role === GUEST_ROLE;

/**
 * Raise-hand state — a participant ATTRIBUTE, not metadata.
 *
 * Value is a unix-ms timestamp string; `''` clears it (LiveKit deletes an
 * attribute key when its value is set to empty, and merges per-key rather than
 * replacing the whole map).
 *
 * Attributes rather than a data packet because they ride along in
 * ParticipantInfo: a moderator who joins after a hand went up observes it with
 * no re-announce, polling, or replay logic. Same "late joiners see it" property
 * that put the room-wide policy on room metadata.
 *
 * Session-scoped (rebuilt from the token's claim on a fresh join) — which is
 * the behaviour we want here. A hand should not survive a rejoin.
 *
 * Deliberately not under the `lk.` prefix; that namespace is reserved by the
 * SDK for agent/transcription keys.
 *
 * Written server-side only. Self-writes would need the `canUpdateOwnMetadata`
 * grant, and granting that would let a participant rewrite their own metadata
 * to forge `role: 'edit'` and slip past the moderator-skipping mute sweep.
 */
export const HAND_RAISED_ATTRIBUTE = 'handRaisedAt';

/**
 * When this participant raised their hand, or undefined if they haven't.
 * The timestamp is what makes the moderator's queue orderable by who asked
 * first, so it's parsed as a number rather than treated as a boolean flag.
 */
export const parseHandRaisedAt = (
  attributes?: Record<string, string> | null,
): number | undefined => {
  const raw = attributes?.[HAND_RAISED_ATTRIBUTE];
  if (!raw) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

/**
 * Metadata as a plain object, never throwing and never returning a non-object.
 *
 * Exported because anything that WRITES metadata has to start from the whole
 * object, not from its own typed slice. `parseRoomPolicy` deliberately narrows
 * to `{ muteAll, cameraOffAll }`, so a writer that round-trips through it drops
 * every other key — which is how a room-wide mute would otherwise wipe the
 * waiting-room queue that now shares this same metadata blob.
 */
export const parseMetadataObject = (
  metadata?: string | null,
): Record<string, any> => {
  if (!metadata) return {};
  try {
    const parsed = JSON.parse(metadata);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, any>;
    }
    return {};
  } catch {
    return {};
  }
};

const parseJson = (metadata?: string | null): any => parseMetadataObject(metadata);

/**
 * Apply a policy change to room metadata, preserving every key this module
 * knows nothing about. Always use this to write the policy — see the warning
 * on `parseMetadataObject`.
 */
export const withRoomPolicy = (
  metadata: string | null | undefined,
  patch: Partial<RoomMediaPolicy>,
): string => JSON.stringify({ ...parseMetadataObject(metadata), ...patch });

export const parseRoomPolicy = (metadata?: string | null): RoomMediaPolicy => {
  const parsed = parseJson(metadata);
  return {
    muteAll: parsed.muteAll === true,
    cameraOffAll: parsed.cameraOffAll === true,
  };
};

export const parseParticipantOverride = (
  metadata?: string | null,
): ParticipantMediaOverride => {
  const parsed = parseJson(metadata);
  return {
    micEnabled: typeof parsed.micEnabled === 'boolean' ? parsed.micEnabled : undefined,
    cameraEnabled:
      typeof parsed.cameraEnabled === 'boolean' ? parsed.cameraEnabled : undefined,
    screenShareEnabled:
      typeof parsed.screenShareEnabled === 'boolean'
        ? parsed.screenShareEnabled
        : undefined,
  };
};

export const parseParticipantRole = (metadata?: string | null): string | undefined => {
  const parsed = parseJson(metadata);
  return typeof parsed.role === 'string' ? parsed.role : undefined;
};

export interface ResolveLockInput {
  /** Tri-state override for this device from participant metadata. */
  individual: boolean | undefined;
  /** Whether the room-wide policy covers this device. */
  roomWide: boolean | undefined;
  /** Moderators are exempt from room-wide policy (never from an individual lock). */
  isModerator: boolean;
  /**
   * Live server-mute signal from a MICROPHONE_CONTROL / CAMERA_CONTROL data
   * packet. Ephemeral — never persisted, so it can't be recovered on rejoin.
   */
  serverMuted?: boolean;
  /**
   * Read-only page permission, or an anonymous link joiner. Locked by default
   * rather than by anyone's action — see `isRestrictedRole`.
   */
  restricted?: boolean;
}

/**
 * Single source of truth for "is this control locked, and why".
 *
 * Precedence, most specific first:
 *   1. individual === false  → locked, personal
 *   2. serverMuted           → locked, personal
 *   3. individual === true   → granted; beats both a room-wide mute and being
 *                              restricted, which is what makes it the one
 *                              mechanism a moderator needs for either case
 *   4. restricted && !moderator → locked, and only a grant clears it
 *   5. room-wide && !moderator  → locked, not personal
 *   6. otherwise             → free
 *
 * (4) sits above (5) deliberately. Both can be true at once, and the messages
 * point somewhere different: "everyone is muted" invites you to wait for that
 * to be lifted, which for a read-only participant never helps — lifting the
 * room-wide mute leaves them exactly as locked as before.
 *
 * A moderator is exempt from (4) and (5) but NOT from (1) or (2) — another
 * moderator muting them deliberately has to stick.
 */
export const resolveMediaLock = ({
  individual,
  roomWide,
  isModerator,
  serverMuted,
  restricted,
}: ResolveLockInput): MediaLock => {
  if (individual === false) return { locked: true, reason: 'disabled' };
  if (serverMuted) return { locked: true, reason: 'server_muted' };
  if (individual === true) return UNLOCKED;
  if (restricted && !isModerator) return { locked: true, reason: 'restricted' };
  if (roomWide && !isModerator) return { locked: true, reason: 'muted_all' };
  return UNLOCKED;
};
